import * as PIXI from 'pixi.js';
import { LayoutContainer, LayoutText } from '@pixi/layout/components'; // eslint-disable-line
import { APP_WIDTH, APP_HEIGHT, CARD_WIDTH, CARD_HEIGHT } from '../../utils';

import TappableTextureButton from '../../common/containers/TappableTextureButton';
import BottomBar, { BOTTOM_BAR_HEIGHT } from '../../common/containers/BottomBar';
import StarterDeckSelectContainer from '../containers/starterDeckSelectContainer';

const LOBBY_UI_Y = 1440;

export default class LobbyManager {
    constructor(selectedDeck, showStarterDeckSelect = false, starterDeckItems = []) {
        this.container = new PIXI.Container();
        this.container.label = 'lobbyContainer';
        this.selectedDeck = selectedDeck;
        this.showStarterDeckSelect = showStarterDeckSelect;
        this.starterDeckItems = starterDeckItems || [];
        this.shopBadgeVisible = false;
        this.width = APP_WIDTH;
        this.height = APP_HEIGHT;
    }

    static hasLoadedAssets() {
        return Boolean(PIXI.Assets.get('lobbyBg'));
    }

    static async loadAssets() {
        await PIXI.Assets.load([
            { src: '/assets/lobby/bg.webp', alias: 'lobbyBg' },
            { src: '/assets/lobby/create_custom.png', alias: 'createCustomButton' },
            { src: '/assets/lobby/join_custom.png', alias: 'joinCustomButton' },
            { src: '/assets/lobby/find_match.png', alias: 'findMatchButton' },
            { src: `/assets/lobby/switch_deck_arrows.png`, alias: 'switchDeckArrows' },
            { src: `/assets/lobby/diamond_container.png`, alias: 'diamondContainer' },
            { src: `/assets/lobby/diamond_selected.png`, alias: 'diamondSelected' },
            { src: '/assets/lobby/chopper_disco.png', alias: 'joinDiscordButton' },
        ]);
        await BottomBar.loadAssets();
    }

    loadStarterDeckAssets() {
        return StarterDeckSelectContainer.loadItemAssets(this.starterDeckItems);
    }

    attachToPixiApp(app) {
        app.stage.addChild(this.container);
    }

    detachFromPixiApp(app) {
        app.stage.removeChild(this.container);
    }

    buildBackground() {
        this.background = new PIXI.Sprite(PIXI.Assets.get('lobbyBg'));
        this.background.label = 'lobbyBg';
        this.background.x = this.width / 2;
        this.background.y = this.height / 2;
        this.background.anchor.set(0.5);
        this.background.scale.set(1.03);
        this.container.addChild(this.background);
    }

    buildFindMatchButton() {
        this.findMatchButtonLayoutContainer = new LayoutContainer();
        this.findMatchButtonLayoutContainer.label = 'findMatchButtonLayoutContainer';
        this.findMatchButtonLayoutContainer.layout = {
            width: 650,
            height: 150,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            paddingLeft: (APP_WIDTH - 950) / 2,
        };
        this.findMatchButtonLayoutContainer.x = this.width / 2 - APP_WIDTH / 2;
        this.findMatchButtonLayoutContainer.y = LOBBY_UI_Y - 7;
        this.container.addChild(this.findMatchButtonLayoutContainer);

        this.findMatchButton = new TappableTextureButton({
            texture: PIXI.Assets.get('findMatchButton'),
            width: 656,
            height: 241,
        });
        this.findMatchButtonLayoutContainer.addChild(this.findMatchButton);

        this.findMatchButton.onButtonTap = () => {
            this.onFindMatchButtonClick?.();
        };
    }

    buildCustomRoomsButtons() {
        this.customRoomsButtonsLayoutContainer = new LayoutContainer();
        this.customRoomsButtonsLayoutContainer.label = 'customRoomsButtonsLayoutContainer';
        this.customRoomsButtonsLayoutContainer.layout = {
            width: APP_WIDTH,
            height: 128,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: (APP_WIDTH - 950) / 2,
            paddingRight: (APP_WIDTH - 950) / 2,
        };
        this.customRoomsButtonsLayoutContainer.x = this.width / 2 - APP_WIDTH / 2;
        this.customRoomsButtonsLayoutContainer.y = LOBBY_UI_Y + 400;
        this.container.addChild(this.customRoomsButtonsLayoutContainer);

        this.createCustomRoomButton = new TappableTextureButton({
            texture: PIXI.Assets.get('createCustomButton'),
            width: 465,
            height: 128,
        });
        this.customRoomsButtonsLayoutContainer.addChild(this.createCustomRoomButton);
        
        this.joinCustomRoomButton = new TappableTextureButton({
            texture: PIXI.Assets.get('joinCustomButton'),
            width: 465,
            height: 128,
        });
        this.customRoomsButtonsLayoutContainer.addChild(this.joinCustomRoomButton);

        this.createCustomRoomButton.onButtonTap = () => {
            this.onCreateCustomRoomButtonClick?.();
        };
        this.joinCustomRoomButton.onButtonTap = () => {
            this.onJoinCustomRoomButtonClick?.();
        };
    }

    buildDeckSelection(rebuild = false) {
        const PADDING = 35;
        const STROKE_WIDTH = 5;

        if (!rebuild) {
            this.deckSelectionLayoutContainer = new LayoutContainer();
            this.deckSelectionLayoutContainer.label = 'deckSelectionLayoutContainer';
            this.deckSelectionLayoutContainer.layout = {
                width: CARD_WIDTH * 1.3,
                height: CARD_HEIGHT * 1.2 + PADDING,
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
            };
            this.deckSelectionLayoutContainer.x = (this.width / 2 - APP_WIDTH / 2) + APP_WIDTH - CARD_WIDTH * 1.3 - (APP_WIDTH - 950) / 2;
            this.deckSelectionLayoutContainer.y = LOBBY_UI_Y;
            this.container.addChild(this.deckSelectionLayoutContainer);
        } else {
            this.deckSelectionLayoutContainer.removeChildren();
        }

        const selectedDeckText = new PIXI.Text({
            text: 'SELECTED DECK',
            style: {
                fontFamily: 'Caladea',
                fontSize: 40,
                fontWeight: '700',
                fill: '#E9bb2d',
            },
        });
        selectedDeckText.x = CARD_WIDTH * 1.3 - (CARD_WIDTH * 1.2 + PADDING) / 2;
        selectedDeckText.y = -10;
        selectedDeckText.anchor.set(0.5, 1);
        this.deckSelectionLayoutContainer.addChild(selectedDeckText);

        const leaderContainerBg = new PIXI.Graphics();
        leaderContainerBg.layout = {
            width: CARD_WIDTH * 1.2 + PADDING,
            height: CARD_HEIGHT * 1.2 + PADDING,
            position: 'absolute',
            top: 0,
            right: 0,
        };
        leaderContainerBg.roundRect(0, 0, CARD_WIDTH * 1.2 + PADDING, CARD_HEIGHT * 1.2 + PADDING, 20);
        leaderContainerBg.fill({ color: '#000000' });
        leaderContainerBg.stroke({ color: '#dba921', width: 5, alignment: 0 });
        this.deckSelectionLayoutContainer.addChild(leaderContainerBg);

        if (!this.selectedDeck) {
            return;
        }

        const leaderTexture = PIXI.Assets.get(`/proxy/image/${this.selectedDeck.leader}.png`);
        this.deckSelectionButton = new TappableTextureButton({
            texture: leaderTexture,
            width: CARD_WIDTH * 1.2,
            height: CARD_HEIGHT * 1.2,
        });
        this.deckSelectionButton.x = -PADDING / 2;
        this.deckSelectionButton.y = PADDING / 2;
        this.deckSelectionLayoutContainer.addChild(this.deckSelectionButton);

        this.deckSelectionButton.onButtonTap = () => {
            this.onDeckSelectionButtonClick?.();
        };

        const switchDeckArrows = new PIXI.Sprite(PIXI.Assets.get('switchDeckArrows'));
        switchDeckArrows.layout = {
            width: 94,
            height: 105,
            position: 'absolute',
            top: 15,
            right: 15,
        };
        switchDeckArrows.eventMode = 'none';
        this.deckSelectionLayoutContainer.addChild(switchDeckArrows);

        const deckNameText = new LayoutText({
            text: this.selectedDeck.name,
            style: {
                fontFamily: 'Open Sans',
                fontSize: 40,
                fontWeight: '600',
                fill: '#ffffff',
                wordWrap: true,
                align: 'center',
            },
        });
        deckNameText.layout = {
            position: 'absolute',
            width: CARD_WIDTH * 1.2 + PADDING - STROKE_WIDTH*2,
            height: 100,
            objectPosition: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            bottom: 40,
            padding: 5,
        };
        deckNameText.x = -STROKE_WIDTH;
        deckNameText.eventMode = 'none';
        this.deckSelectionLayoutContainer.addChild(deckNameText);
    }

    buildMatchTypeSelection() {
        this.matchTypeSelectionLayoutContainer = new LayoutContainer();
        this.matchTypeSelectionLayoutContainer.label = 'matchTypeSelectionLayoutContainer';
        this.matchTypeSelectionLayoutContainer.layout = {
            width: 640,
            height: 84,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        };
        this.matchTypeSelectionLayoutContainer.x = (this.width / 2 - APP_WIDTH / 2) + (APP_WIDTH - 950) / 2;
        this.matchTypeSelectionLayoutContainer.y = LOBBY_UI_Y + 280;
        this.container.addChild(this.matchTypeSelectionLayoutContainer);

        const rankedMatchContainer = new LayoutContainer();
        rankedMatchContainer.label = 'rankedMatchContainer';
        rankedMatchContainer.layout = {
            height: 84,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 25,
        };
        this.matchTypeSelectionLayoutContainer.addChild(rankedMatchContainer);

        const rankedMatchDiamondContainer = new PIXI.Sprite(PIXI.Assets.get('diamondContainer'));
        rankedMatchDiamondContainer.alpha = 0.3;
        rankedMatchDiamondContainer.layout = {
            width: 84,
            height: 84,
        };
        rankedMatchContainer.addChild(rankedMatchDiamondContainer);

        const rankedMatchText = new PIXI.Text({
            text: 'Ranked',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 50,
                fontWeight: '600',
                fill: '#6E6E6E',
            },
        });
        rankedMatchText.layout = {
        };
        rankedMatchText.x = 0;
        rankedMatchText.y = 0;
        rankedMatchContainer.addChild(rankedMatchText);

        const normalmatchContainer = new LayoutContainer();
        normalmatchContainer.label = 'normalmatchContainer';
        normalmatchContainer.layout = {
            height: 84,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 25,
        };
        this.matchTypeSelectionLayoutContainer.addChild(normalmatchContainer);

        const normalMatchDiamondContainer = new PIXI.Sprite(PIXI.Assets.get('diamondContainer'));
        normalMatchDiamondContainer.layout = {
            width: 84,
            height: 84,
        };
        normalmatchContainer.addChild(normalMatchDiamondContainer);

        const normalMatchDiamondSelected = new PIXI.Sprite(PIXI.Assets.get('diamondSelected'));
        normalMatchDiamondSelected.layout = {
            width: 84,
            height: 84,
            position: 'absolute',
            left: 0,
        };
        // normalMatchDiamondSelected.x = -84;
        normalmatchContainer.addChild(normalMatchDiamondSelected);

        const normalmatchText = new PIXI.Text({
            text: 'Normal',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 50,
                fontWeight: '600',
                fill: '#ffffff',
            },
        });
        normalmatchText.layout = {
        };
        normalmatchText.x = 0;
        normalmatchText.y = 0;
        normalmatchContainer.addChild(normalmatchText);
    }

    buildJoinDiscordButton() {
        const width = 200;
        const height = 215;
        const margin = (APP_WIDTH - 950) / 2;

        this.joinDiscordButton = new TappableTextureButton({
            texture: PIXI.Assets.get('joinDiscordButton'),
            width,
            height,
        });
        this.joinDiscordButton.x = margin;
        this.joinDiscordButton.y = margin;
        this.joinDiscordButton.onButtonTap = () => {
            this.onJoinDiscordButtonClick?.();
        };

        this.container.addChild(this.joinDiscordButton);
    }

    buildBottomBar() {
        this.bottomBar = new BottomBar({
            width: this.width,
            selected: 'main',
            onCollectionTap: () => this.onGoToCollectionButtonClick?.(),
            onShopTap: () => this.onGoToShopButtonClick?.(),
            shopBadgeVisible: this.shopBadgeVisible,
        });
        this.bottomBar.y = this.height - BOTTOM_BAR_HEIGHT;
        this.container.addChild(this.bottomBar);
    }

    setShopBadgeVisible(visible) {
        this.shopBadgeVisible = visible;
        this.bottomBar?.setShopBadgeVisible(visible);
    }

    buildStarterDeckSelect(claimingItemId = null) {
        this.starterDeckSelect = new StarterDeckSelectContainer(this.starterDeckItems, this.width, this.height);
        this.starterDeckSelect.visible = this.showStarterDeckSelect;
        this.starterDeckSelect.onClaimItem = (itemId) => {
            this.onClaimStarterDeck?.(itemId);
        };
        if (claimingItemId) {
            this.starterDeckSelect.setClaimingItemId(claimingItemId);
        }
        this.container.addChild(this.starterDeckSelect);
    }

    buildMainLayout(width, height) {
        const claimingItemId = this.starterDeckSelect?.claimingItemId ?? null;
        this.container.removeChildren();

        this.width = width;
        this.height = height;

        this.buildBackground();

        this.buildFindMatchButton();
        this.buildDeckSelection();
        this.buildCustomRoomsButtons();
        this.buildMatchTypeSelection();
        this.buildJoinDiscordButton();
        this.buildBottomBar();
        this.buildStarterDeckSelect(claimingItemId);
    }

    setStarterDeckSelectVisible(visible) {
        this.showStarterDeckSelect = visible;
        if (this.starterDeckSelect) {
            this.starterDeckSelect.visible = visible;
        }
    }

    setClaimingStarterDeck(itemId) {
        this.starterDeckSelect?.setClaimingItemId(itemId);
    }

    setFindingMatch(isFindingMatch) {
        this.findingMatch = isFindingMatch;
    }

    setSelectedDeck(deck) {
        this.selectedDeck = deck;
        if (deck) {
            this.buildDeckSelection(true);
        }
    }
}